import {
  useMutation,
  useQueryClient,
  useQuery,
  UseMutationResult,
  UseQueryResult,
} from 'react-query';
import { TemplatesAPI } from 'data/templates/api';
import { StatusUpdatesTemplatesAPI } from 'data/templates/status-updates/api';
import {
  StatusUpdateTemplate,
  StatusUpdateTemplateDetails,
} from 'data/templates/status-updates/type';
import {
  getTemplatesDetailsQueryKey,
  getStatusUpdateTemplatesQueryKey,
  getTemplatesPublishQueryKey,
  getTemplatesQueryKey,
} from 'queries';

export const useCreateStatusUpdateTemplateMutation = (): UseMutationResult<
  StatusUpdateTemplateDetails,
  unknown,
  StatusUpdateTemplateDetails
> => {
  const queryClient = useQueryClient();

  return useMutation<
    StatusUpdateTemplateDetails,
    unknown,
    StatusUpdateTemplateDetails
  >(
    (payload: StatusUpdateTemplateDetails) =>
      TemplatesAPI.create<StatusUpdateTemplateDetails>(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getStatusUpdateTemplatesQueryKey());
      },
    },
  );
};

export const useGetAllStatusUpdateTemplatesQuery = (): UseQueryResult<
  StatusUpdateTemplate[],
  unknown
> => {
  return useQuery(getStatusUpdateTemplatesQueryKey(), () => {
    return StatusUpdatesTemplatesAPI.findAll();
  });
};

export const useGetNextUpdateTime = (templateId: number) => {
  return useQuery(getTemplatesPublishQueryKey(templateId), () => {
    return TemplatesAPI.getStatusUpdateNextPublish(templateId);
  });
};

export const useGetStatusUpdateTemplateQuery = (
  templateId: number,
): UseQueryResult<StatusUpdateTemplateDetails, unknown> => {
  return useQuery(getTemplatesDetailsQueryKey(templateId), () => {
    return TemplatesAPI.getById(templateId);
  });
};

export const useGetNewStatusUpdateTemplateQuery = (): UseQueryResult<
  StatusUpdateTemplateDetails,
  unknown
> => {
  const queryClient = useQueryClient();

  return useQuery(getTemplatesDetailsQueryKey('new'), () => {
    return queryClient.getQueryData(getTemplatesDetailsQueryKey('new'));
  });
};

export const useUpdateStatusUpdateTemplateMutation = (
  templateId: number,
): UseMutationResult<
  StatusUpdateTemplateDetails,
  unknown,
  Partial<StatusUpdateTemplateDetails>
> => {
  const queryClient = useQueryClient();

  return useMutation<
    StatusUpdateTemplateDetails,
    unknown,
    Partial<StatusUpdateTemplateDetails>
  >(
    (payload: Partial<StatusUpdateTemplateDetails>) =>
      TemplatesAPI.save(payload as StatusUpdateTemplateDetails),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getTemplatesDetailsQueryKey(templateId),
        );
        await queryClient.invalidateQueries(
          getTemplatesPublishQueryKey(templateId),
        );
        await queryClient.invalidateQueries(getStatusUpdateTemplatesQueryKey());
        await queryClient.invalidateQueries(getTemplatesQueryKey());
      },
    },
  );
};
